import { useCallback } from 'react';
import { useAIMode } from '../context/AIModeContext';
import { confirmDialog } from '../components/ui/dialog';

/**
 * useModeSwitch — the single, guarded entry point for changing the AI's posture
 * from any UI surface (the header control bar, the Cockpit posture cards, the
 * Cockpit preset styles).
 *
 * Switching INTO a posture that inflicts actions on the desktop without asking
 * first ('acting') is a consequential change — it can lock the screen, block
 * apps, adjust the economy, change the wallpaper, and so on, all with no
 * per-action approval. So we confirm before entering it. Every other transition
 * (including 'advising', which still queues each action for approval) applies
 * immediately.
 *
 * Returns an async fn: `await switchMode('acting')` → resolves true if the mode
 * was applied, false if the user cancelled the confirmation. Callers can use the
 * result to decide whether to close a popover / mark a preset active.
 */

// Postures that act on the desktop with NO per-action approval gate.
const REQUIRES_CONFIRM = new Set(['acting']);

const CONFIRM_COPY = {
    acting: {
        title: 'Turn on Acting mode?',
        message:
            'The AI will carry out actions on the desktop by itself — locking the screen, ' +
            'blocking apps, adjusting the balance, changing the wallpaper and more — without ' +
            'asking you first. You can switch back to a lower mode at any time.',
        confirmLabel: 'Turn on Acting',
        cancelLabel: 'Cancel',
        danger: true,
    },
};

export function useModeSwitch() {
    const { mode, setMode } = useAIMode();

    return useCallback(async (nextMode) => {
        if (!nextMode || nextMode === mode) return true;
        if (REQUIRES_CONFIRM.has(nextMode)) {
            const confirmed = await confirmDialog(CONFIRM_COPY[nextMode]);
            if (!confirmed) return false;
        }
        setMode(nextMode);
        return true;
    }, [mode, setMode]);
}

export default useModeSwitch;
